// The contributor's payout request dialog (phone, operator, amount).
import { FormEvent, useState } from "react";
import paymentsApi from "@/api/payments.api";
import { formatCurrency } from "../helpers";
import { PROVIDER_TO_NETWORK, apiErrorMessage } from "../logic/payout-logic";
import type { GetPaidBase, GetPaidT, MoneyFormat } from "./get-paid-context";

interface ContributorPayoutRequestInput extends GetPaidBase, MoneyFormat {
  tPage: GetPaidT["getPaidPage"];
  payoutState: { eligible: boolean; earnedAmount: number; approved: number };
  loadMyPayoutRequests: () => Promise<void>;
}

export function useContributorPayoutRequest(input: ContributorPayoutRequestInput) {
  const { t, user, toast, locale, currency, tPage, payoutState, loadMyPayoutRequests } = input;
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [submittingProfileForm, setSubmittingProfileForm] = useState(false);
  const [profileForm, setProfileForm] = useState({
    phone: user?.phoneNumber ?? "",
    provider: "",
    accountName: user?.name ?? "",
    amount: "",
  });

  const openProfileDialog = () => {
    setProfileForm((prev) => ({
      phone: user?.phoneNumber ?? prev.phone,
      provider: prev.provider,
      accountName: user?.name ?? prev.accountName,
      amount: "",
    }));
    setPayoutDialogOpen(true);
  };

  const handleRequestPayout = () => {
    if (!payoutState.eligible) {
      return;
    }
    openProfileDialog();
  };

  const handleCompleteProfile = () => {
    openProfileDialog();
  };

  const handleProfileSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const amount = Number(profileForm.amount);
    if (!profileForm.phone || !profileForm.provider || !profileForm.accountName) {
      toast({ title: tPage.profileDialog.errorTitle, description: tPage.profileDialog.errorDescription, variant: "destructive" });
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: t.amountInvalid, description: t.amountGreaterThanZero, variant: "destructive" });
      return;
    }
    if (amount > payoutState.earnedAmount) {
      toast({
        title: t.getPaid.amountInsufficient,
        description: t.getPaid.amountInsufficientDesc.replace("{amount}", formatCurrency(payoutState.earnedAmount, locale, currency)),
        variant: "destructive",
      });
      return;
    }

    const network = PROVIDER_TO_NETWORK[profileForm.provider];
    if (!network) {
      toast({ title: t.getPaid.operatorNotSupported, description: t.getPaid.operatorNotSupportedDesc, variant: "destructive" });
      return;
    }

    setSubmittingProfileForm(true);
    try {
      await paymentsApi.createPayoutRequest({
        amount: Math.round(amount),
        phone: profileForm.phone.trim(),
        network,
        description: `Demande de paiement - ${payoutState.approved} contributions - ${profileForm.provider}`,
      });
      toast({ title: t.getPaid.requestCreated, description: t.getPaid.requestCreatedDesc });
      setPayoutDialogOpen(false);
      setProfileForm((prev) => ({ ...prev, amount: "" }));
      await loadMyPayoutRequests();
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.createRequestFailed), variant: "destructive" });
    } finally {
      setSubmittingProfileForm(false);
    }
  };

  return {
    payoutDialogOpen, setPayoutDialogOpen,
    profileForm, setProfileForm, submittingProfileForm,
    handleRequestPayout, handleCompleteProfile, handleProfileSubmit,
  };
}
